import { Ruleset } from 'enums/Ruleset';
import { ISessionManagerProvider } from 'interfaces/providers/ISessionManagerProvider';
import type { ICustomStrikeZone } from 'interfaces/session/ICustomStrikeZone';
import { IApiGameLevel, IApiLeague } from 'interfaces/team/IApiScheduleResponse';
import { IPlayer } from 'interfaces/team/IPlayer';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { LineupManager } from 'models/setLineup/LineupManager';
import moment from 'moment';
import { ContextualService } from 'services/data-services/ContextualService';
import { PlayerService } from 'services/data-services/PlayerService';
import { SessionManagerService } from 'services/data-services/SessionManagerService';
import { FeatureConfigService } from 'services/FeatureConfigService';
import { HotkeyService } from 'services/helper-services/HotkeyService';
import { buildSessionStrikeZone } from 'services/helper-services/sessionStrikeZone';
import { ContextStore } from 'stores/ContextStore';
import NoteStore from 'stores/NoteStore';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import { ITaggingProvider } from '../../interfaces/providers/ITaggingProvider';
import { IApiSessionMetadataUpdated } from '../../interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { LogService } from '../../services/data-services/LogService';
import { ExtraInningRulesStore } from './ExtraInningRulesStore';
import { TaggingPlayStore } from './TaggingPlayStore';

export class TaggingStore {
  private taggingService: ITaggingProvider;

  private sessionStore: SessionStore;

  private contextualService: ContextualService;

  private sessionService: ISessionManagerProvider;

  private hotkeyService: HotkeyService;

  private contextStore: ContextStore;

  private extraInningRulesStore: ExtraInningRulesStore;

  private featureConfigService: FeatureConfigService;

  private notificationStore: NotificationStore;

  constructor(
    taggingService: ITaggingProvider,
    sessionStore: SessionStore,
    contextualService: ContextualService,
    sessionService: SessionManagerService,
    logService: LogService,
    hotkeyService: HotkeyService,
    contextStore: ContextStore,
    extraInningRulesStore: ExtraInningRulesStore,
    playerService: PlayerService,
    noteStore: NoteStore,
    featureConfigSevice: FeatureConfigService,
    notificationStore: NotificationStore
  ) {
    this.taggingService = taggingService;
    this.sessionStore = sessionStore;
    this.logService = logService;
    this.playerService = playerService;
    this.noteStore = noteStore;
    this.playStore = new TaggingPlayStore(
      this,
      sessionStore,
      taggingService,
      logService,
      extraInningRulesStore,
      playerService,
      noteStore,
      featureConfigSevice,
      notificationStore
    );
    this.contextualService = contextualService;
    this.sessionService = sessionService;
    this.hotkeyService = hotkeyService;
    this.contextStore = contextStore;
    this.extraInningRulesStore = extraInningRulesStore;
    this.featureConfigService = featureConfigSevice;
    this.notificationStore = notificationStore;
    makeObservable(this);
  }

  @observable isLoading = false;

  @observable isSubstituting = false;

  noteStore: NoteStore;

  playStore: TaggingPlayStore;

  logService: LogService;

  playerService: PlayerService;

  @observable homeTeamRoster: IPlayer[] = [];

  @observable awayTeamRoster: IPlayer[] = [];

  @observable isInitialized = false;

  @action resumeSession = async () => {
    const mode = this.sessionStore.currentSessionMode;
    if (!this.sessionStore.isGameInProgress || !this.sessionStore.isMetadataLineupSet) {
      this.logService.logError(
        {
          message: 'Cannot resume tagging',
          sessionMetadata: this.sessionStore.currentSessionMetadata,
          sessionMode: this.sessionStore.currentSessionMode,
        },
        { function: 'TaggingStore@resumeSession' }
      );
      this.notificationStore.addNotification({
        id: 'resume-tagging-error-1',
        message: 'Cannot resume tagging. Session is in incorrect state or lineup has not been set',
        variant: 'error',
        removable: true,
        onClose: () => {
          this.notificationStore.removeNotification('resume-tagging-error-1');
        },
      });
      return;
    }

    let plays;

    try {
      plays = await this.taggingService.getPlaysWithBallFinals(mode.sessionId!);
      await this.getRostersFromLocalDB(mode.sessionId!);
    } catch (err: unknown) {
      if (err instanceof Error) {
        this.notificationStore.addNotification({
          id: 'resume-tagging-error-2',
          message: err.message,
          variant: 'error',
          removable: true,
          onClose: () => {
            this.notificationStore.removeNotification('resume-tagging-error-2');
          },
        });
      }
    }

    if (plays) {
      this.playStore.initializeState(plays.plays);
    }
    runInAction(() => {
      this.isInitialized = true;
    });
  };

  @action cleanUp = () => {
    this.playStore.dispose();
    this.playStore = new TaggingPlayStore(
      this,
      this.sessionStore,
      this.taggingService,
      this.logService,
      this.extraInningRulesStore,
      this.playerService,
      this.noteStore,
      this.featureConfigService,
      this.notificationStore
    );
  };

  @action initializeFromLineup = async (
    homeTeamManager: LineupManager | undefined,
    awayTeamManager: LineupManager | undefined,
    level: IApiGameLevel,
    league: IApiLeague,
    ruleset: Ruleset,
    customStrikeZone: ICustomStrikeZone | null,
    owningOrganizationId?: string
    // umpire?: IUmpire
  ) => {
    this.isLoading = true;
    const sessionMode = this.sessionStore.currentSessionMode;
    const sessionMetadata = {
      sessionId: sessionMode.sessionId || '',
      owningOrganizationId,
      homeTeam: homeTeamManager ? homeTeamManager.getMetadataTeam() : null,
      awayTeam: awayTeamManager ? awayTeamManager.getMetadataTeam() : null,
      league: league.id && league.id.length > 0 ? league : undefined,
      level: level.id && level.id.length > 0 ? level : undefined,
      ruleSet: ruleset,
      location: {},
      tagger: { id: null },
      sessionTaggingState: {
        state: 'InitialLineupSet',
        sessionStartedUtc: sessionMode.time,
        sessionStartedLocal: '',
        playCountAfterApprovalplayCount: 0,
        initialLineupSetUtc: moment().toISOString(),
        initialLineupSetLocal: new Date(),
      },
      time: moment().toISOString(),
      sessionModeState: {
        state: sessionMode.sessionMode,
        sessionStartedUtc: sessionMode.time,
        sessionStartedLocal: '',
      },
      sessionType: sessionMode.sessionType,
      privateSession: sessionMode.privateSession,
      systemId: sessionMode.systemId,
      isVideoCaptureEnabled: sessionMode.isVideoCaptureEnabled,
      isPlayerTrackingEnabled: sessionMode.isPlayerTrackingEnabled,
      // umpire: umpire,
      strikeZone: buildSessionStrikeZone(league, customStrikeZone),
      isOperatorlessTaggingEnabled: this.sessionStore.currentSessionMetadata.isOperatorlessTaggingEnabled,
      gameScheduleReference: sessionMode.gameScheduleReference,
      dualSystemReference: this.sessionStore.currentSessionMetadata.dualSystemReference,
    } as IApiSessionMetadataUpdated;
    await this.sessionStore.updateSessionMetadata(sessionMetadata, false);

    // If autotag - skip
    if (homeTeamManager && awayTeamManager) {
      await this.submitRostersToLocalDB(sessionMode.sessionId, homeTeamManager.roster, awayTeamManager.roster);
      await this.getRostersFromLocalDB(sessionMode.sessionId!);
      this.playStore.initializeState([]);
    }
    runInAction(() => {
      this.isInitialized = true;
      this.isLoading = false;
    });
  };

  @action submitRostersToLocalDB = async (sessionId: string | undefined, homeTeamRoster: IPlayer[], awayTeamRoster: IPlayer[]) => {
    if (sessionId) {
      return this.sessionService.submitSessionRoster({
        sessionId,
        homeTeamRoster,
        awayTeamRoster,
      });
    }
  };

  @action getRostersFromLocalDB = async (sessionId: string) => {
    const sessionRosters = await this.sessionService.getSessionRoster(sessionId);
    if (!sessionRosters) {
      throw Error("Couldn't fetch rosters");
    }
    this.homeTeamRoster = sessionRosters.homeTeamRoster;
    this.awayTeamRoster = sessionRosters.awayTeamRoster;
  };

  @action disableEnableHotkeys = (disabled: boolean) => {
    if (disabled) {
      this.hotkeyService.pause();
    } else {
      this.hotkeyService.resume();
    }
  };

  @observable isTagging = false;

  @action setIsTagging(isTagging: boolean) {
    this.isTagging = isTagging;
  }
}
